import { api } from '@shared/api';
import type { PlanTier } from '@entities/Plan';
import type { Role } from '@shared/types';

// Доменные типы (camelCase). Бэк отдаёт camelCase, response-интерцептор
// (snakeizeKeys) рекурсивно переводит весь ответ в snake_case — поэтому ниже
// читаем snake и маппим в чистый camel (как в dashboardApi).

/** Траты на генерацию карточек: «сегодня» — с 00:00 МСК, «месяц» — последние
 *  periodDays дней. Рубли — по реальным токенам из журнала llm_calls. */
export interface AdminSpend {
  generationsToday: number;
  generationsMonth: number;
  /** Вызовов модели за месяц, включая повторы. */
  callsMonth: number;
  /** Вызовов сверх первого в генерации (второй, третий...). */
  extraCallsMonth: number;
  rubToday: number;
  rubMonth: number;
  rubAvgDay: number;
  /** Сколько из rubMonth ушло на повторные вызовы. */
  extraRubMonth: number;
  /** Из rubMonth — ходы игры и проверка грамматики (2026-11-01); генерации и вызовы выше — только слова. */
  playRubMonth: number;
}

export interface AdminUserSpendRow extends AdminSpend {
  id: string;
  name: string;
  email: string;
  plan: PlanTier;
  role: Role;
  isEmailVerified: boolean;
  createdAt: string;
}

export interface AdminSpendTotals extends AdminSpend {
  /** Генерации месяца по числу вызовов модели. */
  jobsOneCall: number;
  jobsTwoCalls: number;
  jobsThreePlusCalls: number;
}

export interface AdminDashboard {
  users: AdminUserSpendRow[];
  totals: AdminSpendTotals;
  payments: { unreconciledCaptures: number; unreconciledAmountRub: number };
  /** Первая запись журнала токенов; до неё траты не учитывались. */
  measuredSince: string | null;
  periodDays: number;
  usdToRub: number;
  generatedAt: string;
}

interface BackendSpend {
  generations_today: number;
  generations_month: number;
  calls_month: number;
  extra_calls_month: number;
  rub_today: number;
  rub_month: number;
  rub_avg_day: number;
  extra_rub_month: number;
  play_rub_month?: number;
}

interface BackendAdminDashboard {
  users: (BackendSpend & {
    id: string;
    name: string;
    email: string;
    plan: PlanTier;
    role: Role;
    is_email_verified: boolean;
    created_at: string;
  })[];
  totals: BackendSpend & {
    jobs_one_call: number;
    jobs_two_calls: number;
    jobs_three_plus_calls: number;
  };
  payments: { unreconciled_captures: number; unreconciled_amount_rub: number };
  measured_since: string | null;
  period_days: number;
  usd_to_rub: number;
  generated_at: string;
}

const toSpend = (s: BackendSpend): AdminSpend => ({
  generationsToday: s.generations_today,
  generationsMonth: s.generations_month,
  callsMonth: s.calls_month,
  extraCallsMonth: s.extra_calls_month,
  rubToday: s.rub_today,
  rubMonth: s.rub_month,
  rubAvgDay: s.rub_avg_day,
  extraRubMonth: s.extra_rub_month,
  playRubMonth: s.play_rub_month ?? 0,
});

/** GET /api/v1/admin/dashboard — все пользователи с тратами на генерацию. Гейт на
 *  бэке: не-админ получит 403 NOT_ADMIN (react-query пробросит как isError). */
export async function fetchAdminDashboard(): Promise<AdminDashboard> {
  const { data } = await api.get<BackendAdminDashboard>('/admin/dashboard');
  return {
    users: data.users.map((u) => ({
      ...toSpend(u),
      id: u.id,
      name: u.name,
      email: u.email,
      plan: u.plan,
      role: u.role,
      isEmailVerified: u.is_email_verified,
      createdAt: u.created_at,
    })),
    totals: {
      ...toSpend(data.totals),
      jobsOneCall: data.totals.jobs_one_call,
      jobsTwoCalls: data.totals.jobs_two_calls,
      jobsThreePlusCalls: data.totals.jobs_three_plus_calls,
    },
    payments: {
      unreconciledCaptures: data.payments.unreconciled_captures,
      unreconciledAmountRub: data.payments.unreconciled_amount_rub,
    },
    measuredSince: data.measured_since,
    periodDays: data.period_days,
    usdToRub: data.usd_to_rub,
    generatedAt: data.generated_at,
  };
}
